import React, { useState, useMemo } from 'react';
import {
  FormControl,
  Autocomplete,
  TextField,
  CircularProgress,
  Button,
  Box,
  Typography,
} from '@mui/material';
import {
  AccountTree as AccountTreeIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { CompanyGroup } from '../../models/CompanyGroup';
import { useSearchCompanyGroupsAutocomplete } from '../../hooks/useCompanyGroup';
import { zoullooColors } from '../../theme';

interface CompanyGroupAutocompleteProps {
  value?: CompanyGroup | { id: number; name: string } | null;
  disabled?: boolean;
  error?: string;
  showCreateButton?: boolean;
  onChange: (group: CompanyGroup | null) => void;
  onCreateNew?: () => void;
}

const CompanyGroupAutocomplete: React.FC<CompanyGroupAutocompleteProps> = ({
  value,
  disabled = false,
  error,
  showCreateButton = false,
  onChange,
  onCreateNew,
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');

  const { data: searchedGroups = [], isLoading: isSearching } = useSearchCompanyGroupsAutocomplete(
    searchTerm,
    2
  );

  const options = useMemo(() => {
    if (searchTerm.length < 2) {
      return [];
    }

    return searchedGroups
      .filter((group, index, self) =>
        index === self.findIndex(g => g.id === group.id)
      )
      .sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', undefined, {
          sensitivity: 'base',
          numeric: true
        })
      );
  }, [searchedGroups, searchTerm]);

  const handleInputChange = (_: React.SyntheticEvent, newInputValue: string) => {
    setSearchTerm(newInputValue);
  };

  const handleChange = (_: unknown, newValue: CompanyGroup | null) => {
    onChange(newValue);
  };

  const getNoOptionsText = () => {
    if (searchTerm.length < 2) {
      return t('companyGroup.searchMinChars');
    }
    return t('companyGroup.noGroupsFound');
  };

  return (
    <FormControl fullWidth error={!!error}>
      <Box>
        <Autocomplete
          value={value as CompanyGroup | null}
          onChange={handleChange}
          onInputChange={handleInputChange}
          options={options}
          getOptionLabel={(option) => option.name || ''}
          isOptionEqualToValue={(option, val) => option.id === val.id}
          loading={isSearching && searchTerm.length >= 2}
          loadingText={t('common.loading')}
          noOptionsText={getNoOptionsText()}
          disabled={disabled}
          slotProps={{
            paper: {
              elevation: 8,
              sx: {
                zIndex: 1300,
                backgroundColor: 'background.paper',
              }
            },
            popper: {
              sx: {
                zIndex: 1300,
              }
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t('company.selectGroup')}
              error={!!error}
              helperText={error}
              sx={{
                '& .MuiInputBase-root': {
                  height: '56px',
                  minHeight: '56px',
                },
                '& .MuiInputBase-input': {
                  height: 'auto',
                  padding: '16.5px 14px',
                },
              }}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <SearchIcon sx={{ color: 'action.active', mr: 1, my: 0.5 }} />
                    {params.InputProps.startAdornment}
                  </>
                ),
                endAdornment: (
                  <>
                    {isSearching && searchTerm.length >= 2 ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}

          renderOption={(props, option) => (
            <Box component="li" {...props}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <AccountTreeIcon sx={{ color: 'action.active', mr: 1 }} />
                <Typography variant="body1">
                  {option.name}
                </Typography>
              </Box>
            </Box>
          )}
        />
      </Box>

      {showCreateButton && !disabled && onCreateNew && (
        <Button
          onClick={onCreateNew}
          sx={{
            mt: 1,
            alignSelf: 'flex-start',
            color: zoullooColors.secondary.main,
            '&:hover': {
              backgroundColor: zoullooColors.secondary.alpha
            }
          }}
          startIcon={<AccountTreeIcon />}
          size="small"
        >
          {t('company.createNewGroup')}
        </Button>
      )}
    </FormControl>
  );
};

export default CompanyGroupAutocomplete;
